import { Button, TextButton } from '../ui';
import { OtpInput } from './OtpInput';
import { useCodeVerification } from '../../../lib/use-code-verification';
import { wixLogout } from '../../../lib/logout';
import { useTranslation } from '../../../lib/i18n';
import styles from './CodeScreen.module.css';

type CodeVerification = ReturnType<typeof useCodeVerification>;

// The code step's fields. Rendered only by the verification dialog now — the full-screen version is gone
// and `/onboarding/code` forwards to `/welcome` (see onboarding-gate's RETIRED_ONBOARDING). The hook is
// passed in rather than called again: the dialog's heading needs the number it resolves, and a second
// call would mount a second, independent copy of the step's state.
export function CodeStepBody({ code }: { code: CodeVerification }) {
  const { t } = useTranslation();
  const sending = code.resend === 'sending';

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        code.submit();
      }}
    >
      <OtpInput code={code} invalid={code.errorKind === 'code'} />

      {code.error && !code.phoneInUse && (
        <p className={styles.error} role="alert">
          {code.error}
        </p>
      )}

      {code.phoneInUse ? (
        <div className={styles.inUse} role="alert">
          <p className={styles.inUseTitle}>{t('onboarding.code.phoneInUse.title')}</p>
          <p className={styles.inUseBody}>{t('onboarding.code.phoneInUse.body')}</p>
          <div className={styles.inUseActions}>
            <Button
              variant="outlined"
              onClick={() => {
                code.logPageClickBi('phone_in_use_use_another');
                code.toPhoneStep();
              }}
            >
              {t('onboarding.code.phoneInUse.useAnother')}
            </Button>
            <Button
              variant="text"
              onClick={() => {
                code.logPageClickBi('phone_in_use_sign_in_other');
                wixLogout();
              }}
            >
              {t('onboarding.code.phoneInUse.signInOther')}
            </Button>
          </div>
        </div>
      ) : (
        <>
          <div className={styles.verify}>
            <Button size="large" fullWidth disabled={!code.complete} loading={code.submitting} onClick={code.submit}>
              {t('onboarding.code.symphony.cta')}
            </Button>
          </div>

          {/* Both are disabled while either send is in flight — otherwise the idle one
              looks live and swallows the click. */}
          <div className={styles.resend}>
            <Button
              variant="outlined"
              size="large"
              disabled={sending}
              loading={sending && code.method !== 'whatsapp'}
              onClick={() => void code.sendCode('sms', 'resend_sms')}
            >
              {t('onboarding.code.symphony.resend.sms')}
            </Button>
            <Button
              variant="outlined"
              size="large"
              disabled={sending}
              loading={sending && code.method === 'whatsapp'}
              onClick={() => void code.sendCode('whatsapp', 'resend_whatsapp')}
            >
              {t('onboarding.code.symphony.resend.whatsapp')}
            </Button>
          </div>

          {/* Always mounted so a resend never reflows the card, and it is the live
              region — the frame draws no confirmation, but a user who pressed
              Resend needs to see that something happened. */}
          <p className={styles.resent} aria-live="polite">
            {code.resend === 'sent' ? t('onboarding.code.symphony.resent') : ''}
          </p>

          <div className={styles.footer}>
            <TextButton
              size="small"
              underline
              onClick={() => {
                code.logPageClickBi('use_different_number');
                code.toPhoneStep();
              }}
            >
              {t('onboarding.code.useDifferentNumber')}
            </TextButton>
          </div>
        </>
      )}
    </form>
  );
}
